import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRequests } from '../contexts/RequestsContext';
import type { LeaveType, LeaveRequest, OvertimeRequest, SalaryAdvanceRequest, ShiftSwapRequest, Colleague, RequestStatus, ApiLeaveType } from '../types';
import { getStatusVariant, getLeaveTypes, getColleagues } from '../services/requests';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  CalendarDays, Clock4, DollarSign, Plus, Trash2, Loader2,
  FileText, CheckCircle, XCircle, Clock, AlertCircle,
  CalendarRange, Hourglass, Wallet, ArrowRight, Ban, X, ArrowLeftRight, Users,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function formatRupiah(amount: number): string {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
}

const LEAVE_TYPES: LeaveType[] = [
  'Annual Leave', 'Sick Leave', 'Personal Leave', 'Emergency Leave', 'Maternity/Paternity Leave', 'Unpaid Leave',
];

const LEAVE_TYPE_COLORS: Record<LeaveType, string> = {
  'Annual Leave': 'bg-primary/10 text-primary',
  'Sick Leave': 'bg-destructive/10 text-destructive',
  'Personal Leave': 'bg-accent-light text-accent',
  'Emergency Leave': 'bg-warning-light text-warning',
  'Maternity/Paternity Leave': 'bg-success-light text-success',
  'Unpaid Leave': 'bg-muted text-muted-foreground',
};

// ---------- Leave Form ----------
function LeaveForm({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { submitLeave } = useRequests();
  const { t } = useLanguage();
  const [apiLeaveTypes, setApiLeaveTypes] = useState<ApiLeaveType[]>([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void getLeaveTypes().then((types) => {
      setApiLeaveTypes(types);
      if (types.length > 0) setSelectedLeaveTypeId(types[0].id);
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedLeaveTypeId || !startDate || !endDate || !reason.trim()) {
      toast.error(t.fillAllFields);
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error(t.endDateAfterStart);
      return;
    }
    setSubmitting(true);
    await submitLeave({ leaveTypeId: selectedLeaveTypeId, startDate, endDate, reason: reason.trim() });
    toast.success(t.leaveSubmitted);
    setSubmitting(false);
    onClose();
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="leave-type" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.leaveType}</Label>
        <Select
          value={selectedLeaveTypeId !== null ? String(selectedLeaveTypeId) : ''}
          onValueChange={(v) => setSelectedLeaveTypeId(Number(v))}
        >
          <SelectTrigger data-testid="leave-type-select">
            <SelectValue placeholder={apiLeaveTypes.length === 0 ? t.loading : t.selectLeaveType} />
          </SelectTrigger>
          <SelectContent>
            {apiLeaveTypes.map((lt) => (
              <SelectItem key={lt.id} value={String(lt.id)}>
                {lt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="start-date" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.startDate}</Label>
          <Input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="leave-start-date" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="end-date" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.endDate}</Label>
          <Input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="leave-end-date" />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="leave-reason" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.reason}</Label>
        <Textarea id="leave-reason" placeholder={t.reasonPlaceholder} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="leave-reason" />
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl h-11" data-testid="leave-cancel-btn">{t.cancel}</Button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-md disabled:opacity-60 active:scale-[0.98] transition-transform"
          style={{ background: 'var(--gradient-primary)' }}
          data-testid="leave-submit-btn"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.submitRequest}
        </button>
      </div>
    </form>
  );
}

// ---------- Overtime Form ----------
function OvertimeForm({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { submitOvertime } = useRequests();
  const { t } = useLanguage();
  const [date, setDate] = useState('');
  const [totalHours, setTotalHours] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const hours = parseFloat(totalHours);
    if (!date || isNaN(hours) || hours <= 0) {
      toast.error(t.fillValidValues);
      return;
    }
    if (hours > 12) {
      toast.error(t.otMaxExceeded);
      return;
    }
    setSubmitting(true);
    await submitOvertime({ date, totalHours: hours });
    toast.success(t.overtimeSubmitted);
    setSubmitting(false);
    onClose();
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="ot-date" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.date}</Label>
        <Input id="ot-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="overtime-date" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ot-hours" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.otTotalHours}</Label>
        <Input id="ot-hours" type="number" min="0.5" max="12" step="0.5" placeholder="e.g. 2.5" value={totalHours} onChange={(e) => setTotalHours(e.target.value)} data-testid="overtime-hours" />
        <p className="text-[11px] text-muted-foreground">{t.otMaxHours}</p>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl h-11" data-testid="overtime-cancel-btn">{t.cancel}</Button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-md disabled:opacity-60 active:scale-[0.98] transition-transform"
          style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}
          data-testid="overtime-submit-btn"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.submitRequest}
        </button>
      </div>
    </form>
  );
}

// ---------- Salary Advance Form ----------
function SalaryAdvanceForm({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { submitSalaryAdvance } = useRequests();
  const { t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0 || !reason.trim()) {
      toast.error(t.fillValidValues);
      return;
    }
    setSubmitting(true);
    await submitSalaryAdvance({ amount: parsedAmount, reason: reason.trim() });
    toast.success(t.salarySubmitted);
    setSubmitting(false);
    onClose();
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="sa-amount" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.amount}</Label>
        <div className="flex">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm">Rp</span>
          <Input
            id="sa-amount"
            type="number"
            min="1"
            step="1"
            placeholder="1.000.000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="rounded-l-none"
            data-testid="salary-amount"
          />
        </div>
        {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
          <p className="text-xs text-muted-foreground">{formatRupiah(parseFloat(amount))}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="sa-reason" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{t.reason}</Label>
        <Textarea id="sa-reason" placeholder={t.salaryReasonPlaceholder} value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="salary-reason" />
      </div>
      <div className="flex items-center gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl h-11" data-testid="salary-cancel-btn">{t.cancel}</Button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-md disabled:opacity-60 active:scale-[0.98] transition-transform"
          style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }}
          data-testid="salary-submit-btn"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.submitRequest}
        </button>
      </div>
    </form>
  );
}

// ---------- Shift Swap Form ----------
function ShiftSwapForm({ onClose }: { onClose: () => void }): React.JSX.Element {
  const { submitShiftSwap } = useRequests();
  const { t } = useLanguage();
  const [colleagues, setColleagues] = useState<Colleague[]>([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [swapDate, setSwapDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Re-fetch colleagues with shift filter whenever the date changes.
  useEffect(() => {
    if (!swapDate) {
      setColleagues([]);
      setTargetId('');
      return;
    }
    setLoadingColleagues(true);
    setTargetId('');
    getColleagues(swapDate)
      .then(setColleagues)
      .catch(() => setColleagues([]))
      .finally(() => setLoadingColleagues(false));
  }, [swapDate]);

  const selectedColleague = colleagues.find((c) => String(c.id) === targetId) ?? null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!targetId || !swapDate) { toast.error(t.fillAllFields); return; }
    setSubmitting(true);
    try {
      await submitShiftSwap({ targetId: Number(targetId), swapDate, reason: reason || undefined });
      toast.success(t.shiftSwapSubmitted);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.fillValidValues);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      {/* Swap Date */}
      <div className="space-y-2">
        <Label htmlFor="swap-date" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
          {t.swapDate}
        </Label>
        <Input
          id="swap-date"
          type="date"
          min={today}
          value={swapDate}
          onChange={(e) => setSwapDate(e.target.value)}
          required
        />
      </div>

      {/* Target Employee */}
      <div className="space-y-2">
        <Label htmlFor="target-employee" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
          {t.targetEmployee}
        </Label>
        {!swapDate ? (
          <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input text-sm text-muted-foreground bg-muted/30">
            <CalendarDays className="h-3.5 w-3.5" />
            {t.selectDateFirst}
          </div>
        ) : loadingColleagues ? (
          <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t.loadingColleagues}
          </div>
        ) : colleagues.length === 0 ? (
          <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-input text-sm text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            {t.noColleaguesFound}
          </div>
        ) : (
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger id="target-employee">
              <SelectValue placeholder={t.selectColleague} />
            </SelectTrigger>
            <SelectContent>
              {colleagues.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  <span className="flex items-center gap-2">
                    <span>{c.fullName}</span>
                    <span className="text-[10px]">•</span>
                    {c.shiftTime}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Shift info card for selected colleague */}
        {selectedColleague?.shiftName && (
          <div className="flex items-center gap-2.5 p-2.5 rounded-xl border border-cyan-200 dark:border-cyan-900 bg-cyan-50 dark:bg-cyan-950/30">
            <div
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
              style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)' }}
            >
              <ArrowLeftRight className="h-3.5 w-3.5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-cyan-700 dark:text-cyan-400 font-bold uppercase tracking-widest">{t.targetShift}</p>
              <p className="text-sm font-black text-cyan-900 dark:text-cyan-100 leading-tight">{selectedColleague.shiftName}</p>
            </div>
          </div>
        )}
      </div>

      {/* Reason */}
      <div className="space-y-2">
        <Label htmlFor="swap-reason" className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
          {t.swapReason}
        </Label>
        <Textarea
          id="swap-reason"
          placeholder={t.swapReasonPlaceholder}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
        />
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl h-11">{t.cancel}</Button>
        <button
          type="submit"
          disabled={submitting || loadingColleagues || !swapDate}
          className="flex-[2] h-11 flex items-center justify-center gap-2 rounded-xl font-bold text-white text-sm shadow-md disabled:opacity-60 active:scale-[0.98] transition-transform"
          style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.submitRequest}
        </button>
      </div>
    </form>
  );
}

// ---------- Status Badge with icon ----------
function StatusBadge({ status }: { status: RequestStatus }): React.JSX.Element {
  const { t } = useLanguage();
  const config: Record<string, { icon: React.ElementType; variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; className: string }> = {
    pending: { icon: Clock, variant: 'secondary', label: t.pendingRequests, className: 'bg-secondary/20 text-secondary-foreground border-secondary/30' },
    approved: { icon: CheckCircle, variant: 'default', label: t.approved, className: 'bg-success/10 text-success border-success/20' },
    rejected: { icon: XCircle, variant: 'destructive', label: t.rejected, className: 'bg-destructive/10 text-destructive border-destructive/20' },
    cancelled: { icon: XCircle, variant: 'outline', label: t.cancelled, className: 'bg-muted text-muted-foreground border-muted' },
  };
  const { icon: Icon, label, className } = config[status];
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${className}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {label}
    </div>
  );
}

// ---------- Empty State ----------
function EmptyState({ label, icon: Icon }: { label: string; icon: React.ElementType }): React.JSX.Element {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-state">
      <div className="relative mb-5">
        <div
          className="h-20 w-20 rounded-2xl flex items-center justify-center"
          style={{ background: 'rgba(240,130,0,0.08)' }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="h-8 w-8 text-primary" style={{ opacity: 0.45 }} />
        </div>
      </div>
      <p className="text-sm font-bold text-foreground mb-1">{t.noRequests(label)}</p>
      <p className="text-xs text-muted-foreground">{t.noRequestsDesc}</p>
    </div>
  );
}

// ---------- Main Page ----------
export default function Requests(): React.JSX.Element {
  const {
    leaveRequests, overtimeRequests, salaryAdvanceRequests, shiftSwapRequests, loading,
    cancelLeave, cancelOvertime, cancelSalaryAdvance, cancelShiftSwap, refreshAll,
  } = useRequests();
  const { t } = useLanguage();

  // Load requests data on mount (lazy — skips if already fetched by Profile).
  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [overtimeDialogOpen, setOvertimeDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [shiftSwapDialogOpen, setShiftSwapDialogOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'leave' | 'overtime' | 'salary' | 'shiftswap'>('leave');
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  useEffect(() => {
    const updateIndicator = () => {
      const container = tabContainerRef.current;
      if (!container) return;
      const buttons = container.querySelectorAll('button[data-tab-role="button"]');
      const activeIndex = (['leave', 'overtime', 'salary', 'shiftswap'] as const).indexOf(activeTab);
      const activeBtn = buttons[activeIndex] as HTMLElement | undefined;
      if (!activeBtn) return;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeBtn.getBoundingClientRect();
      setIndicatorStyle({
        left: activeRect.left - containerRect.left,
        width: activeRect.width,
        opacity: 1,
      });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab, leaveRequests.length, overtimeRequests.length, salaryAdvanceRequests.length, shiftSwapRequests.length]);

  // Shared cancel-confirmation state
  const [cancelConfirm, setCancelConfirm] = useState<{ open: boolean; onConfirm: (() => void) | null }>({
    open: false,
    onConfirm: null,
  });

  const confirmCancel = (onConfirm: () => void): void => {
    setCancelConfirm({ open: true, onConfirm });
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatRelative = (iso: string): string => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(iso);
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    const all = [
      ...leaveRequests.map((r) => r.status),
      ...overtimeRequests.map((r) => r.status),
      ...salaryAdvanceRequests.map((r) => r.status),
      ...shiftSwapRequests.map((r) => r.status),
    ];
    return {
      total: all.length,
      pending: all.filter((s) => s === 'pending').length,
      approved: all.filter((s) => s === 'approved').length,
      rejected: all.filter((s) => s === 'rejected').length,
      cancelled: all.filter((s) => s === 'cancelled').length,
    };
  }, [leaveRequests, overtimeRequests, salaryAdvanceRequests, shiftSwapRequests]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="requests-loading">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t.loadingRequests}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in pb-8" data-testid="requests-page">

      {/* ─── Dialogs (state-controlled) ─── */}

      {/* Leave Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:hidden">
          <div className="relative overflow-hidden px-5 py-4" style={{ background: 'var(--gradient-primary)' }}>
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                  <CalendarDays className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.myRequests}</p>
                  <p className="text-[15px] font-black text-white leading-tight">{t.newLeaveTitle}</p>
                </div>
              </div>
              <button onClick={() => setLeaveDialogOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Close">
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="text-[11px] text-white/70 mt-2 font-medium">{t.newLeaveDesc}</p>
          </div>
          <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
            <LeaveForm onClose={() => setLeaveDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Overtime Dialog */}
      <Dialog open={overtimeDialogOpen} onOpenChange={setOvertimeDialogOpen}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:hidden">
          <div className="relative overflow-hidden px-5 py-4" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}>
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                  <Clock4 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.myRequests}</p>
                  <p className="text-[15px] font-black text-white leading-tight">{t.newOvertimeTitle}</p>
                </div>
              </div>
              <button onClick={() => setOvertimeDialogOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Close">
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="text-[11px] text-white/70 mt-2 font-medium">{t.newOvertimeDesc}</p>
          </div>
          <div className="px-5 py-4">
            <OvertimeForm onClose={() => setOvertimeDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Salary Advance Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:hidden">
          <div className="relative overflow-hidden px-5 py-4" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }}>
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                  <Wallet className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.myRequests}</p>
                  <p className="text-[15px] font-black text-white leading-tight">{t.newSalaryTitle}</p>
                </div>
              </div>
              <button onClick={() => setSalaryDialogOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Close">
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="text-[11px] text-white/70 mt-2 font-medium">{t.newSalaryDesc}</p>
          </div>
          <div className="px-5 py-4">
            <SalaryAdvanceForm onClose={() => setSalaryDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Swap Dialog */}
      <Dialog open={shiftSwapDialogOpen} onOpenChange={setShiftSwapDialogOpen}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:hidden">
          <div className="relative overflow-hidden px-5 py-4" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-black/10" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                  <ArrowLeftRight className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.myRequests}</p>
                  <p className="text-[15px] font-black text-white leading-tight">{t.newShiftSwapTitle}</p>
                </div>
              </div>
              <button onClick={() => setShiftSwapDialogOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Close">
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            <p className="text-[11px] text-white/70 mt-2 font-medium">{t.newShiftSwapDesc}</p>
          </div>
          <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">
            <ShiftSwapForm onClose={() => setShiftSwapDialogOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Cancel Confirmation ─── */}
      <AlertDialog
        open={cancelConfirm.open}
        onOpenChange={(open) => !open && setCancelConfirm({ open: false, onConfirm: null })}
      >
        <AlertDialogContent className="max-w-sm p-0 overflow-hidden rounded-3xl border-0 shadow-2xl">
          <div className="relative overflow-hidden px-5 py-5" style={{ background: 'linear-gradient(135deg, hsl(var(--destructive)) 0%, #991b1b 100%)' }}>
            <div className="pointer-events-none absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/10" />
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 shadow-inner flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[11px] font-bold text-white/70 uppercase tracking-[0.15em]">KONFIRMASI</p>
                <p className="text-[15px] font-black text-white leading-tight">{t.cancelRequest}</p>
              </div>
            </div>
          </div>
          <div className="px-5 py-5">
            <p className="text-sm text-muted-foreground mb-5">{t.cancelRequestDesc}</p>
            <div className="flex items-center gap-3">
              <AlertDialogCancel className="flex-1 rounded-xl h-11" data-testid="confirm-cancel-no">{t.keepRequest}</AlertDialogCancel>
              <AlertDialogAction
                className="flex-[2] h-11 rounded-xl font-bold bg-destructive hover:bg-destructive/90 text-destructive-foreground inline-flex items-center justify-center gap-2"
                onClick={() => { cancelConfirm.onConfirm?.(); setCancelConfirm({ open: false, onConfirm: null }); }}
                data-testid="confirm-cancel-yes"
              >
                <Trash2 className="h-4 w-4" />
                {t.yesCancel}
              </AlertDialogAction>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* ═══════════════════════════════════════
          HERO HEADER
      ═══════════════════════════════════════ */}
      <div
        className="relative rounded-2xl overflow-hidden shadow-lg"
        style={{ background: 'var(--gradient-primary)' }}
      >
        <div className="pointer-events-none absolute -top-10 -right-10 h-44 w-44 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-10 -left-8 h-36 w-36 rounded-full bg-black/10" />

        <div className="relative px-5 pt-5 pb-6">
          {/* Title row */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20 shadow-inner">
                <FileText className="h-4 w-4 text-white" />
              </div>
              <span className="text-[11px] font-bold text-white/80 uppercase tracking-[0.15em]">{t.myRequests}</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-[26px] font-black text-white leading-tight tracking-tight">{t.totalRequests}</h1>
              <span
                className="inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-sm font-black text-white/90"
                style={{ background: 'rgba(0,0,0,0.20)' }}
              >
                {summaryStats.total}
              </span>
            </div>
            <p className="text-[12px] text-white/70 mt-0.5 font-medium">{t.myRequestsDesc}</p>
          </div>

          {/* Stat tiles */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: t.pendingRequests, value: summaryStats.pending, Icon: Hourglass },
              { label: t.approved, value: summaryStats.approved, Icon: CheckCircle },
              { label: t.rejected, value: summaryStats.rejected, Icon: XCircle },
              { label: t.cancelled, value: summaryStats.cancelled, Icon: Ban },
            ].map(({ label, value, Icon }) => (
              <div key={label} className="rounded-xl p-3 text-center" style={{ background: 'rgba(0,0,0,0.18)' }}>
                <Icon className="h-4 w-4 mx-auto mb-1.5 text-white/70" />
                <p className="text-2xl font-black text-white leading-none">{value}</p>
                <p className="text-[10px] text-white/65 mt-1 font-semibold leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* ═══════════════════════════════════════
          TABS
      ═══════════════════════════════════════ */}
      <div data-testid="requests-tabs">
        {/* Tab bar */}
        <div
          ref={tabContainerRef}
          className="relative flex gap-1.5 p-1 rounded-2xl border border-border/50 bg-muted/40"
        >
          {/* Sliding active indicator */}
          <div
            className="absolute top-1 bottom-1 rounded-xl transition-all duration-300 ease-out"
            style={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              background: 'var(--gradient-primary)',
              boxShadow: '0 2px 8px rgba(232,130,0,0.35)',
              opacity: indicatorStyle.opacity,
            }}
          />

          {[
            { value: 'leave' as const, label: t.leave, Icon: CalendarDays, count: leaveRequests.length, testId: 'tab-leave' },
            { value: 'overtime' as const, label: t.overtime, Icon: Clock4, count: overtimeRequests.length, testId: 'tab-overtime' },
            { value: 'salary' as const, label: t.salaryAdv, Icon: DollarSign, count: salaryAdvanceRequests.length, testId: 'tab-salary' },
            { value: 'shiftswap' as const, label: t.shiftSwap, Icon: ArrowLeftRight, count: shiftSwapRequests.length, testId: 'tab-shiftswap' },
          ].map(({ value, label, Icon, count, testId }) => (
            <button
              key={value}
              data-tab-role="button"
              onClick={() => setActiveTab(value)}
              className={`relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-bold transition-colors duration-200 ${
                activeTab === value ? 'text-white' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={testId}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
              {count > 0 && (
                <span
                  className={`inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-black transition-colors duration-200 ${
                    activeTab === value
                      ? 'bg-white/25 text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="mt-4">

          {/* ── LEAVE ── */}
          {activeTab === 'leave' && (
            leaveRequests.length === 0 ? (
              <EmptyState label={t.leave} icon={CalendarDays} />
            ) : (
              <div className="space-y-3">
                {leaveRequests.map((r: LeaveRequest) => (
                  <div
                    key={r.id}
                    className="relative rounded-2xl border border-border/50 bg-white dark:bg-card shadow-sm overflow-hidden"
                    data-testid={`leave-item-${r.id}`}
                  >
                    <div className="h-0.5 w-full" style={{
                      background: r.status === 'approved' ? 'linear-gradient(90deg,#10b981,#34d399)'
                        : r.status === 'rejected' ? 'linear-gradient(90deg,#ef4444,#f87171)'
                        : r.status === 'pending' ? 'var(--gradient-primary)'
                        : 'hsl(var(--muted))',
                    }} />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${LEAVE_TYPE_COLORS[r.leaveType as LeaveType] ?? 'bg-muted text-muted-foreground'}`}>
                            {r.leaveType}
                          </span>
                          <StatusBadge status={r.status} />
                        </div>
                        {r.status === 'pending' && (
                          <button
                            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                            onClick={() => confirmCancel(() => void cancelLeave(r.id))}
                            data-testid={`cancel-leave-${r.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 p-3 mb-3 rounded-xl bg-muted/30 border border-border/40">
                        <div className="text-center flex-1">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{t.from}</p>
                          <p className="text-sm font-black text-foreground mt-0.5">{formatDate(r.startDate)}</p>
                        </div>
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <div
                            className="flex h-6 w-6 items-center justify-center rounded-full"
                            style={{ background: 'var(--gradient-primary)' }}
                          >
                            <span className="text-[9px] font-black text-white">{r.totalDays}d</span>
                          </div>
                        </div>
                        <div className="text-center flex-1">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{t.to}</p>
                          <p className="text-sm font-black text-foreground mt-0.5">{formatDate(r.endDate)}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{r.reason}</p>
                      <p className="text-[10px] text-muted-foreground/60 font-semibold">{t.submitted(formatRelative(r.submittedAt))}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── OVERTIME ── */}
          {activeTab === 'overtime' && (
            overtimeRequests.length === 0 ? (
              <EmptyState label={t.overtime} icon={Clock4} />
            ) : (
              <div className="space-y-3">
                {overtimeRequests.map((r: OvertimeRequest) => (
                  <div
                    key={r.id}
                    className="relative rounded-2xl border border-border/50 bg-white dark:bg-card shadow-sm overflow-hidden"
                    data-testid={`overtime-item-${r.id}`}
                  >
                    <div className="h-0.5 w-full" style={{
                      background: r.status === 'approved' ? 'linear-gradient(90deg,#10b981,#34d399)'
                        : r.status === 'rejected' ? 'linear-gradient(90deg,#ef4444,#f87171)'
                        : r.status === 'pending' ? 'linear-gradient(90deg,#f59e0b,#fbbf24)'
                        : 'hsl(var(--muted))',
                    }} />
                    <div className="p-4 flex items-center gap-4">
                      <div
                        className="flex h-14 w-14 flex-shrink-0 flex-col items-center justify-center rounded-2xl"
                        style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}
                      >
                        <p className="text-xl font-black text-white leading-none">{r.totalHours}</p>
                        <p className="text-[9px] text-white/70 font-bold uppercase">{t.hours}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-foreground">{t.overtime}</span>
                          <StatusBadge status={r.status} />
                        </div>
                        <p className="text-xs font-semibold text-foreground">{formatDate(r.date)}</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5 font-semibold">{t.submitted(formatRelative(r.submittedAt))}</p>
                      </div>
                      {r.status === 'pending' && (
                        <button
                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                          onClick={() => confirmCancel(() => void cancelOvertime(r.id))}
                          data-testid={`cancel-overtime-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── SALARY ADVANCE ── */}
          {activeTab === 'salary' && (
            salaryAdvanceRequests.length === 0 ? (
              <EmptyState label={t.salaryAdvance} icon={Wallet} />
            ) : (
              <div className="space-y-3">
                {salaryAdvanceRequests.map((r: SalaryAdvanceRequest) => (
                  <div
                    key={r.id}
                    className="relative rounded-2xl border border-border/50 bg-white dark:bg-card shadow-sm overflow-hidden"
                    data-testid={`salary-item-${r.id}`}
                  >
                    <div className="h-0.5 w-full" style={{
                      background: r.status === 'approved' ? 'linear-gradient(90deg,#10b981,#34d399)'
                        : r.status === 'rejected' ? 'linear-gradient(90deg,#ef4444,#f87171)'
                        : r.status === 'pending' ? 'linear-gradient(90deg,#8b5cf6,#6d28d9)'
                        : 'hsl(var(--muted))',
                    }} />
                    <div className="p-4 flex items-center gap-4">
                      <div
                        className="flex h-14 w-14 flex-shrink-0 flex-col items-center justify-center rounded-2xl"
                        style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' }}
                      >
                        <p className="text-[9px] text-white/60 font-bold uppercase leading-none">IDR</p>
                        <p className="text-base font-black text-white leading-tight">
                          {r.amount >= 1_000_000 ? `${(r.amount / 1_000_000).toFixed(1)}jt` : r.amount >= 1_000 ? `${(r.amount / 1_000).toFixed(0)}rb` : String(r.amount)}
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-foreground">{formatRupiah(r.amount)}</span>
                          <StatusBadge status={r.status} />
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{r.reason}</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-0.5 font-semibold">{t.submitted(formatRelative(r.submittedAt))}</p>
                      </div>
                      {r.status === 'pending' && (
                        <button
                          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                          onClick={() => confirmCancel(() => void cancelSalaryAdvance(r.id))}
                          data-testid={`cancel-salary-${r.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* ── SHIFT SWAP ── */}
          {activeTab === 'shiftswap' && (
            shiftSwapRequests.length === 0 ? (
              <EmptyState label={t.shiftSwap} icon={ArrowLeftRight} />
            ) : (
              <div className="space-y-3">
                {shiftSwapRequests.map((r: ShiftSwapRequest) => (
                  <div
                    key={r.id}
                    className="relative rounded-2xl border border-border/50 bg-white dark:bg-card shadow-sm overflow-hidden"
                    data-testid={`shiftswap-item-${r.id}`}
                  >
                    <div className="h-0.5 w-full" style={{
                      background: r.status === 'approved' ? 'linear-gradient(90deg,#10b981,#34d399)'
                        : r.status === 'rejected' ? 'linear-gradient(90deg,#ef4444,#f87171)'
                        : 'linear-gradient(90deg,#06b6d4,#0891b2)',
                    }} />
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-xl flex-shrink-0" style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)' }}>
                            <ArrowLeftRight className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-foreground leading-none">{r.targetName}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{t.swapDate}: {r.swapDate}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <StatusBadge status={r.status} />
                          {r.status === 'pending' && (
                            <button
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
                              onClick={() => confirmCancel(() => void cancelShiftSwap(r.id))}
                              data-testid={`cancel-shiftswap-${r.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 p-3 mb-3 rounded-xl bg-muted/30 border border-border/40">
                        <div className="flex-1 text-center">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{t.requesterShift}</p>
                          <p className="text-xs font-black text-foreground mt-0.5">{r.requesterShiftName}</p>
                        </div>
                        <div className="flex h-6 w-6 items-center justify-center rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)' }}>
                          <ArrowLeftRight className="h-3 w-3 text-white" />
                        </div>
                        <div className="flex-1 text-center">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold">{t.targetShift}</p>
                          <p className="text-xs font-black text-foreground mt-0.5">{r.targetShiftName}</p>
                        </div>
                      </div>
                      {r.reason && <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{r.reason}</p>}
                      {r.status === 'rejected' && r.rejectionReason && (
                        <p className="text-xs text-destructive/80 mb-1"><span className="font-bold">{t.shiftSwapRejected}:</span> {r.rejectionReason}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground/60 font-semibold">{t.submitted(formatRelative(r.submittedAt))}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

        </div>
      </div>

      {createPortal(
        <>
          {/* ── FAB backdrop ── */}
          {fabOpen && (
            <div
              className="fixed inset-0 z-30 bg-black/25"
              onClick={() => setFabOpen(false)}
            />
          )}

          {/* ── FAB Speed Dial (portalled to body to escape parent transform) ── */}
          <div className="fixed bottom-[5.5rem] right-4 z-40 h-14 w-14">
        {/* Speed dial items — absolutely positioned above the FAB button */}
        <div
          className="absolute right-0 flex flex-col items-end gap-3"
          style={{ bottom: 'calc(100% + 0.75rem)' }}
        >
          {[
            { label: t.newLeave,     Icon: CalendarDays,   bg: 'var(--gradient-primary)',                onClick: () => { setLeaveDialogOpen(true);     setFabOpen(false); }, testId: 'new-leave-btn'    },
            { label: t.newOvertime,  Icon: Clock4,         bg: 'linear-gradient(135deg,#f59e0b,#f97316)', onClick: () => { setOvertimeDialogOpen(true);  setFabOpen(false); }, testId: 'new-overtime-btn' },
            { label: t.newRequest,   Icon: Wallet,         bg: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', onClick: () => { setSalaryDialogOpen(true);    setFabOpen(false); }, testId: 'new-salary-btn'   },
            { label: t.newShiftSwap, Icon: ArrowLeftRight, bg: 'linear-gradient(135deg,#06b6d4,#0891b2)', onClick: () => { setShiftSwapDialogOpen(true); setFabOpen(false); }, testId: 'new-shiftswap-btn' },
          ].map(({ label, Icon, bg, onClick, testId }, idx) => (
            <div
              key={testId}
              className="flex items-center gap-3 transition-all duration-200"
              style={{
                opacity: fabOpen ? 1 : 0,
                transform: fabOpen ? 'translateY(0)' : 'translateY(8px)',
                transitionDelay: fabOpen ? `${(3 - idx) * 50}ms` : '0ms',
                pointerEvents: fabOpen ? 'auto' : 'none',
              }}
            >
              <span className="text-xs font-bold px-3 py-1.5 rounded-full shadow-md bg-background border border-border/50 whitespace-nowrap select-none">
                {label}
              </span>
              <button
                onClick={onClick}
                className="h-12 w-12 rounded-full flex items-center justify-center shadow-lg active:scale-90 transition-transform flex-shrink-0"
                style={{ background: bg }}
                data-testid={testId}
              >
                <Icon className="h-5 w-5 text-white" />
              </button>
            </div>
          ))}
        </div>

        {/* Main FAB button */}
        <button
          onClick={() => setFabOpen((prev) => !prev)}
          className="h-14 w-14 rounded-full flex items-center justify-center active:scale-90 transition-transform duration-200"
          style={{ background: 'var(--gradient-primary)', boxShadow: '0 6px 24px rgba(240,130,0,0.5)' }}
          data-testid="fab-main-btn"
          aria-label="Pengajuan Baru"
        >
          <Plus
            className="h-7 w-7 text-white transition-transform duration-300"
            style={{ transform: fabOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
          />
        </button>
      </div>
        </>,
        document.body,
      )}
    </div>
  );
}
